import { useEffect, useState } from 'react';
import {
  ActionIcon,
  Box,
  Button,
  Group,
  Paper,
  Stack,
  Text,
  Textarea,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconMessageCircle, IconX } from '@tabler/icons-react';
import {
  getCustomerPortalFeedbackStatus,
  postCustomerPortalFeedback,
  dismissCustomerPrompt,
} from '../../lib/api.customer';
import { useTrackClick } from '../../lib/tracking/hooks';

const LABELS = ['Sehr schlecht', 'Schlecht', 'Okay', 'Gut', 'Sehr gut'] as const;

/**
 * Ab hier ist ein Hinweis Pflicht — wortgleich mit der Serverregel in
 * `backend/src/lib/customerFeedbackPolicy.ts`. Eine nackte 2 sagt niemandem, was zu tun
 * ist; ein Satz dazu schon. Der Server lehnt das ohnehin ab (422), aber niemand soll
 * erst auf „Senden" drücken müssen, um das zu erfahren.
 */
const COMMENT_REQUIRED_AT_OR_BELOW = 3;
const MIN_COMMENT_LENGTH = 10;

export function PortalFeedbackRail() {
  const trackClick = useTrackClick();
  const [score, setScore] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  // null = Status noch nicht geladen → Widget bleibt verborgen, bis der Server entschieden hat.
  // true = nicht zeigen: in diesem Quartal beantwortet, heute weggeklickt oder Mitarbeiter-Ansicht.
  const [submitted, setSubmitted] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    getCustomerPortalFeedbackStatus()
      .then((res) => {
        if (!cancelled) setSubmitted(!(res.show ?? !res.alreadySubmitted));
      })
      .catch(() => {
        // Status-Abfrage fehlgeschlagen → Widget anzeigen, damit Feedback möglich bleibt.
        if (!cancelled) setSubmitted(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const commentRequired = score != null && score <= COMMENT_REQUIRED_AT_OR_BELOW;
  const commentTooShort = comment.trim().length < MIN_COMMENT_LENGTH;
  const blockedByComment = commentRequired && commentTooShort;

  async function handleSubmit() {
    if (score == null || blockedByComment) return;
    trackClick('portal-feedback-submit', {
      score,
      hasComment: comment.trim().length > 0,
    });
    setSubmitting(true);
    try {
      await postCustomerPortalFeedback({
        score,
        comment: comment.trim() || undefined,
      });
      notifications.show({
        title: 'Danke!',
        message: 'Dein Feedback hilft uns, das Portal zu verbessern.',
        color: 'green',
      });
      setScore(null);
      setComment('');
      setSubmitted(true);
    } catch (e) {
      notifications.show({
        title: 'Senden fehlgeschlagen',
        message: e instanceof Error ? e.message : 'Bitte später erneut versuchen.',
        color: 'red',
      });
    } finally {
      setSubmitting(false);
    }
  }

  // Status noch nicht geladen, bereits abgestimmt oder manuell geschlossen → Widget nicht anzeigen.
  if (submitted !== false || dismissed) return null;

  return (
    <Box
      style={{
        position: 'fixed',
        zIndex: 100,
        right: 'max(20px, env(safe-area-inset-right, 0px))',
        bottom: 'max(20px, env(safe-area-inset-bottom, 0px))',
        maxWidth: 'min(100vw - 40px, 380px)',
        maxHeight:
          'calc(100dvh - 40px - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))',
        overflowY: 'auto',
        overscrollBehavior: 'contain',
      }}
    >
      <Paper shadow="md" p="sm" radius="lg" withBorder>
        <Stack gap="xs">
          <Group gap="xs" align="flex-start" wrap="nowrap" justify="space-between">
            <Group gap="xs" wrap="wrap" style={{ flex: 1 }}>
              <IconMessageCircle size={18} style={{ flexShrink: 0, opacity: 0.7 }} />
              <Text size="sm" fw={600} style={{ flex: '1 1 120px', minWidth: 0 }}>
                Wie hilfreich ist dieses Portal für dich?
              </Text>
            </Group>
            <ActionIcon
              variant="subtle"
              color="gray"
              size="xs"
              aria-label="Schließen"
              onClick={() => {
                setDismissed(true);
                void dismissCustomerPrompt('portalFeedback').catch(() => undefined);
              }}
              style={{ flexShrink: 0 }}
            >
              <IconX size={14} />
            </ActionIcon>
          </Group>
          <Group gap={6} justify="space-between" wrap="nowrap">
            {LABELS.map((label, i) => {
              const value = i + 1;
              const active = score === value;
              return (
                <Tooltip key={value} label={label} position="top" withArrow>
                  <UnstyledButton
                    onClick={() => setScore(value)}
                    aria-label={label}
                    style={{
                      flex: 1,
                      minWidth: 0,
                      padding: '6px 0',
                      borderRadius: 8,
                      border: active
                        ? '2px solid var(--mantine-color-orange-filled)'
                        : '1px solid var(--mantine-color-default-border)',
                      background: active ? 'var(--mantine-color-orange-light)' : undefined,
                      fontWeight: 700,
                      fontSize: 13,
                      textAlign: 'center',
                    }}
                  >
                    {value}
                  </UnstyledButton>
                </Tooltip>
              );
            })}
          </Group>
          {score != null ? (
            <Stack gap="xs">
              <Textarea
                placeholder={
                  commentRequired
                    ? 'Was war nicht gut? Ein, zwei Sätze reichen.'
                    : 'Optional: Kurzer Kommentar …'
                }
                label={commentRequired ? 'Was sollen wir ändern?' : undefined}
                description={
                  commentRequired
                    ? 'Bei dieser Bewertung brauchen wir den Hinweis — ohne ihn können wir nichts ändern.'
                    : undefined
                }
                withAsterisk={commentRequired}
                error={
                  commentRequired && comment.trim().length > 0 && commentTooShort
                    ? 'Bitte etwas ausführlicher.'
                    : undefined
                }
                value={comment}
                onChange={(e) => setComment(e.currentTarget.value)}
                minRows={2}
                maxLength={2000}
                autosize
                disabled={submitting}
              />
              <Button
                size="xs"
                onClick={handleSubmit}
                loading={submitting}
                disabled={blockedByComment}
              >
                Feedback senden
              </Button>
            </Stack>
          ) : null}
        </Stack>
      </Paper>
    </Box>
  );
}
